/* HER WORTHI CATEGORY, read from what she locked in My Offer.
 *
 * The lock is PROSE that Brill wrote (api/_lib/prompts.js, "WORTHI Category —
 * primary category named, plus one-line description … If she straddles two,
 * include the secondary"). Hers, as locked:
 *
 *     Primary: W — Wealth
 *     Money, business income, …
 *     Secondary: I — Identity
 *
 * Other locks will say it other ways ("Primary category: Health", "Wealth
 * (primary) with Identity as a secondary"), so this reads tolerantly and
 * returns NULL rather than a guess when it cannot tell. A wrong lane is worse
 * than none: it would steer a whole page at the wrong reader, silently.
 *
 * Sites uses it to say what a page leans on (spec §0). The WORD "WORTHI" and
 * the lane names are fine inside Brill — she locks that name in My Offer — and
 * never appear on her public page (80c15d7).
 */

export const WORTHI_LANES = [
  { id: "wealth", letter: "W", label: "Wealth", hint: "Money, income, freedom" },
  { id: "opportunity", letter: "O", label: "Opportunity", hint: "Career, business, next step" },
  { id: "relationships", letter: "R", label: "Relationships", hint: "Love, family, connection" },
  { id: "time", letter: "T", label: "Time", hint: "Freedom, ease, relief" },
  { id: "health", letter: "H", label: "Health", hint: "Body, energy, wellbeing" },
  { id: "identity", letter: "I", label: "Identity", hint: "Confidence, purpose, self" },
];

const byName = (word) => {
  const w = String(word || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return null;
  // "Relationship" as well as "Relationships".
  return WORTHI_LANES.find((l) => l.id === w || l.id.replace(/s$/, "") === w) || null;
};

const NAMES = "Wealth|Opportunity|Relationships?|Time|Health|Identity";

/** The lane named after a "primary" / "secondary" label, if there is one. */
function labelled(text, label) {
  const re = new RegExp(`${label}[^\\n:]*:\\s*(?:\\*\\*)?\\s*(?:[WORTHI]\\s*[—–-]\\s*)?(${NAMES})\\b`, "i");
  const m = String(text).match(re);
  if (m) return byName(m[1]);
  // "Wealth (primary)" / "Identity as a secondary"
  const after = new RegExp(`\\b(${NAMES})\\b[^.\\n]{0,20}?\\b${label}\\b`, "i");
  const n = String(text).match(after);
  return n ? byName(n[1]) : null;
}

/**
 * @param text  the locked "WORTHI Category" output, as prose
 * @returns { primary, secondary } — each a lane from WORTHI_LANES or null;
 *          null overall when no lane can be read at all
 */
export function readWorthi(text) {
  const t = String(text || "");
  if (!t.trim()) return null;
  let primary = labelled(t, "primary");
  const secondary = labelled(t, "secondary");
  if (!primary) {
    /* No "primary" label: the first lane NAMED in the text, as long as it is
       not the one already called secondary. A lock that names one lane and
       nothing else is the common short form. */
    const all = [...t.matchAll(new RegExp(`\\b(${NAMES})\\b`, "gi"))].map((m) => byName(m[1])).filter(Boolean);
    primary = all.find((l) => l.id !== secondary?.id) || null;
  }
  if (!primary && !secondary) return null;
  return { primary, secondary: secondary && secondary.id !== primary?.id ? secondary : null };
}

/** "Wealth + Identity", "Health", or "" — how Brill shows it to her. */
export function worthiLabel(w) {
  if (!w?.primary) return "";
  return w.secondary ? `${w.primary.label} + ${w.secondary.label}` : w.primary.label;
}
